import { createDebugger } from '../utils/debug.js';

const debug = createDebugger('lightbox', false);

class EskLightbox extends HTMLElement {
    constructor() {
        super();
        this._items = [];
        this._currentIndex = 0;
        this._touchStartX = null;
        this._returnFocusTo = null;
        this._onDocumentClick = this._handleDocumentClick.bind(this);
        this._onKeydown = this._handleKeydown.bind(this);
    }

    connectedCallback() {
        this._img = this.querySelector('.lightbox__img');
        this._backdrop = this.querySelector('.lightbox__backdrop');
        this._prevBtn = this.querySelector('.lightbox__prev');
        this._nextBtn = this.querySelector('.lightbox__next');
        this._closeBtn = this.querySelector('.lightbox__close');
        this._counterCurrent = this.querySelector('.lightbox__counter-current');
        this._counterTotal = this.querySelector('.lightbox__counter-total');

        this._prevBtn.addEventListener('click', () => this._prev());
        this._nextBtn.addEventListener('click', () => this._next());
        this._closeBtn.addEventListener('click', () => this.close());
        this._backdrop.addEventListener('click', () => this.close());

        document.addEventListener('click', this._onDocumentClick);
        document.addEventListener('keydown', this._onKeydown);

        this.addEventListener('touchstart', (e) => {
            this._touchStartX = e.changedTouches[0].screenX;
        }, { passive: true });

        this.addEventListener('touchend', (e) => {
            if (this._touchStartX === null) return;
            const dx = e.changedTouches[0].screenX - this._touchStartX;
            if (Math.abs(dx) > 50) dx < 0 ? this._next() : this._prev();
            this._touchStartX = null;
        }, { passive: true });
    }

    disconnectedCallback() {
        document.removeEventListener('click', this._onDocumentClick);
        document.removeEventListener('keydown', this._onKeydown);
        document.documentElement.style.overflow = '';
    }

    _handleDocumentClick(e) {
        const trigger = e.target.closest('[data-lightbox-trigger]');
        if (!trigger) return;

        const group = trigger.dataset.lightboxGroup;
        const index = parseInt(trigger.dataset.lightboxIndex || '0', 10);
        const triggers = [...document.querySelectorAll(
            `[data-lightbox-trigger][data-lightbox-group="${group}"]`
        )];

        if (!triggers.length) return;

        e.preventDefault();
        this._openGroup(triggers, index);
    }

    _handleKeydown(e) {
        if (!this.hasAttribute('open')) return;
        if (e.key === 'Escape') this.close();
        else if (e.key === 'ArrowLeft') this._prev();
        else if (e.key === 'ArrowRight') this._next();
    }

    _openGroup(triggers, startIndex) {
        triggers.sort((a, b) =>
            parseInt(a.dataset.lightboxIndex || 0) - parseInt(b.dataset.lightboxIndex || 0)
        );
        this._items = triggers.map((el) => ({
            src: el.dataset.lightboxSrc || '',
            alt: el.dataset.lightboxAlt || '',
        }));

        const renderedIndex = triggers.findIndex(
            (t) => parseInt(t.dataset.lightboxIndex || 0) === startIndex
        );
        this._open(renderedIndex >= 0 ? renderedIndex : 0);
    }

    _open(index = 0) {
        this._returnFocusTo = document.activeElement;
        this._currentIndex = Math.max(0, Math.min(index, this._items.length - 1));
        this.setAttribute('open', '');
        this.setAttribute('aria-hidden', 'false');
        document.documentElement.style.overflow = 'hidden';
        this._render();
        this._closeBtn.focus();
    }

    close() {
        this.removeAttribute('open');
        this.setAttribute('aria-hidden', 'true');
        document.documentElement.style.overflow = '';
        this._returnFocusTo?.focus({ preventScroll: true });
    }

    _prev() {
        if (this._currentIndex > 0) {
            this._currentIndex--;
            this._render();
        }
    }

    _next() {
        if (this._currentIndex < this._items.length - 1) {
            this._currentIndex++;
            this._render();
        }
    }

    _render() {
        const item = this._items[this._currentIndex];
        if (!item) return;

        const probe = new Image();
        probe.src = item.src;
        const cached = probe.complete;

        if (!cached) this._img.classList.add('lightbox__img--loading');

        this._img.onload = () => {
            this._img.classList.remove('lightbox__img--loading');
            this._prefetchAdjacent();
        };
        this._img.onerror = () => this._img.classList.remove('lightbox__img--loading');
        this._img.src = item.src;
        this._img.alt = item.alt;

        this._counterCurrent.textContent = this._currentIndex + 1;
        this._counterTotal.textContent = this._items.length;

        this._prevBtn.disabled = this._currentIndex === 0;
        this._nextBtn.disabled = this._currentIndex === this._items.length - 1;
    }

    _prefetchAdjacent() {
        [this._currentIndex - 1, this._currentIndex + 1].forEach((i) => {
            if (i >= 0 && i < this._items.length) {
                new Image().src = this._items[i].src;
            }
        });
    }
}

if (!customElements.get('esk-lightbox')) {
    customElements.define('esk-lightbox', EskLightbox);
}

export default EskLightbox;
